/**
 * The shell's heavier parts (popovers, menus, dialogs: the kit's Radix components) in their own chunks, out of the
 * first load (F14 perf, K17). A chunk is requested when the shell first renders its part, i.e. with the shell's
 * first render, not on a click; a placeholder of its size stands in meanwhile, so nothing jumps.
 *
 * A chunk that fails to load (offline, a deployment that replaced the files) is not retried: recovering means
 * reloading the page. A small part keeps its (invisible) placeholder and the rest of the shell works; a part that
 * is the whole page (the create-workspace screen) shows the error state with "Try again", which reloads.
 * `preloadShellParts` loads them all (tests).
 */
import { Component, lazy, Suspense, useState, type ComponentProps, type ComponentType, type ReactNode } from "react";
import { ServerErrorState } from "@brevalt/app-core";
import { SwitcherPlaceholder } from "./workspace";
// Types only (erased): the modules themselves load on demand.
import type { AccountMenu as AccountMenuC } from "./account-menu";
import type { ShellWorkspaceSwitcher as SwitcherC } from "./workspace-switcher";
import type { CreateWorkspace as CreateWorkspaceC } from "./create-workspace";
import type { AssistantLauncher as AssistantLauncherC } from "../assistant/launcher";

/** A part whose chunk failed to load (offline, a new deployment) keeps its placeholder instead of the error page. */
class PartBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }
  override render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

const loaders: (() => Promise<unknown>)[] = [];

/** Loads every deferred part now (tests render the shell without waiting for its chunks). */
export function preloadShellParts(): Promise<unknown[]> {
  return Promise.all(loaders.map((l) => l()));
}

export function deferred<P extends object>(
  load: () => Promise<ComponentType<P>>,
  /** A class for an empty box of the part's size, or the part's look without its behaviour (`preload` on intent). */
  placeholder: string | null | ((props: P, preload: () => void) => ReactNode),
  /** What shows when the chunk failed (default: the placeholder). */
  failed?: ReactNode,
): ComponentType<P> {
  // Once the chunk is in (a first render, or `preloadShellParts`), a new mount renders the part at once.
  let loaded: ComponentType<P> | null = null;
  const loadOnce = () =>
    load().then((C) => {
      loaded = C;
      return C;
    });
  loaders.push(loadOnce);
  const Lazy = lazy(() => loadOnce().then((C) => ({ default: C })));
  function Deferred(props: P) {
    const fallback =
      typeof placeholder === "function" ? (
        placeholder(props, () => {
          void loadOnce().catch(() => undefined);
        })
      ) : placeholder ? (
        <span aria-hidden className={placeholder} />
      ) : null;
    // Chosen once per mount (F15 review M1): switching from `Lazy` to the loaded component on a later render would
    // remount the part (a form's input gone, an open menu closed, focus lost).
    const [Ready] = useState(() => loaded);
    return (
      <PartBoundary fallback={failed ?? fallback}>
        {Ready ? (
          <Ready {...props} />
        ) : (
          <Suspense fallback={fallback}>
            <Lazy {...props} />
          </Suspense>
        )}
      </PartBoundary>
    );
  }
  return Deferred;
}

export const NotificationBell = deferred(
  () => import("./notifications").then((m) => m.NotificationBell),
  "inline-block size-touch lg:size-9",
);
export const AccountMenu = deferred<ComponentProps<typeof AccountMenuC>>(
  () => import("./account-menu").then((m) => m.AccountMenu),
  "block h-14 w-full",
);
export const AccountActions = deferred(() => import("./account-menu").then((m) => m.AccountActions), null);
export const ShellWorkspaceSwitcher = deferred<ComponentProps<typeof SwitcherC>>(
  () => import("./workspace-switcher").then((m) => m.ShellWorkspaceSwitcher),
  (props, preload) => <SwitcherPlaceholder me={props.me} onIntent={preload} />,
);
export const CreateWorkspace = deferred<ComponentProps<typeof CreateWorkspaceC>>(
  () => import("./create-workspace").then((m) => m.CreateWorkspace),
  null,
  // The whole page: not a blank one when its chunk failed (F15 review L2).
  <ServerErrorState
    onRetry={() => {
      window.location.reload();
    }}
  />,
);
export const AssistantTopBarButton = deferred(
  () => import("../assistant/launcher").then((m) => m.AssistantTopBarButton),
  "inline-block size-touch lg:size-9",
);
export const SecondFactorOffer = deferred(
  () => import("../workspace/second-factor-offer").then((m) => m.SecondFactorOffer),
  null,
);
export const ActionResultNotice = deferred(
  () => import("../workspace/action-result").then((m) => m.ActionResultNotice),
  null,
);
export const AssistantLauncher = deferred<ComponentProps<typeof AssistantLauncherC>>(
  () => import("../assistant/launcher").then((m) => m.AssistantLauncher),
  null,
);
